import { useContext, useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { GET_PLAYER_PROMOS } from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import { useAppSelector } from 'hooks';

import {
  getActivePromoResetDays,
  getNextPromoRefreshAt,
  PROMO_RESET_REFRESH_JITTER_MS
} from './promoResetSchedule';

/**
 * Refetches promos one minute after each weekly promo reset (EN-11002).
 *
 * Promo data only reaches the client on login, on opening the promo details,
 * or on a server push after a table charge or account event. Nothing fires at
 * the week boundary, so a session left open across it kept showing last week's
 * hours until the player happened to trigger a fetch.
 *
 * Mobile WebViews pause timers while the app is backgrounded (and some clocks
 * stop during device sleep), so the timer alone misses the most common way a
 * session spans the reset. A foreground catch-up refetches if the scheduled
 * time has already passed, same pattern as the token refresh in App.tsx.
 */
const PromoWeeklyResetRefresh = () => {
  const { socket } = useContext(SocketContext);
  const playerGUID = useAppSelector((state) => state.user?.playerGUID);
  // A string, so the routine promo pushes after every table charge do not
  // reschedule the timer unless a reset day actually changes.
  const resetDaysKey = useAppSelector((state) =>
    getActivePromoResetDays(state.playerPromos?.promotions).join(',')
  );

  useEffect(() => {
    if (!socket || !resetDaysKey) return;

    const resolvedPlayerGUID = playerGUID || localStorage.getItem('playerGUID') || '';
    if (!resolvedPlayerGUID) return;

    const resetDays = resetDaysKey.split(',').map(Number);
    let timer: ReturnType<typeof setTimeout> | undefined;
    let dueAt: number | null = null;

    const scheduleNextRefresh = () => {
      clearTimeout(timer);
      const refreshAt = getNextPromoRefreshAt(resetDays, Date.now());
      if (refreshAt === null) {
        dueAt = null;
        return;
      }

      // Jitter spreads every open session's fetch instead of landing them all
      // on getPlayerPromos in the same second.
      dueAt = refreshAt + Math.floor(Math.random() * PROMO_RESET_REFRESH_JITTER_MS);
      timer = setTimeout(refreshNow, dueAt - Date.now());
    };

    function refreshNow() {
      socket.emit(GET_PLAYER_PROMOS, { playerGUID: resolvedPlayerGUID });
      scheduleNextRefresh();
    }

    const catchUpIfDue = () => {
      if (dueAt !== null && Date.now() >= dueAt) refreshNow();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') catchUpIfDue();
    };

    scheduleNextRefresh();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Capacitor's native resume signal; WebViews don't always fire
    // visibilitychange at the moment the app returns to the foreground.
    let disposed = false;
    let removeNativeListener: (() => void) | null = null;
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('appStateChange', (state: { isActive: boolean }) => {
        if (state.isActive) catchUpIfDue();
      })
        .then((handle) => {
          if (disposed) handle.remove();
          else removeNativeListener = () => handle.remove();
        })
        .catch(() => {
          /* noop */
        });
    }

    return () => {
      disposed = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      removeNativeListener?.();
    };
  }, [playerGUID, resetDaysKey, socket]);

  return null;
};

export default PromoWeeklyResetRefresh;
